/**
 * Blow Out Babe - Custom JavaScript Enhancements & Error Prevention
 * Fixes:
 * 1. Prevents "Missing shadow root" errors in <overflow-list>
 * 2. Prevents "Required ref 'cartBubbleCount' not found" errors in <cart-icon>
 * 3. Prevents "Unexpected EOF" JSON parsing crashes
 * 4. Fixes dead clicks on Checkout & Buy Now buttons and removes navigation blockers
 */

(function () {
  'use strict';

  /* ==========================================================================
     1. Safe JSON Parsing (Eliminates "Unexpected EOF" & "Unexpected token <")
     ========================================================================== */
  if (typeof Response !== 'undefined' && Response.prototype && Response.prototype.json) {
    const originalResponseJson = Response.prototype.json;
    Response.prototype.json = async function () {
      try {
        const text = await this.text();
        if (!text || !text.trim()) {
          return {};
        }
        return JSON.parse(text);
      } catch (err) {
        console.warn('[custom.js] Handled invalid JSON/EOF response safely:', err);
        return {};
      }
    };
  }

  const originalJSONParse = JSON.parse;
  JSON.parse = function (text, reviver) {
    if (typeof text === 'string' && (!text || !text.trim())) {
      return null;
    }
    try {
      return originalJSONParse(text, reviver);
    } catch (err) {
      if (
        err instanceof SyntaxError &&
        (err.message.includes('EOF') ||
          err.message.includes('end of JSON') ||
          err.message.includes('Unexpected token'))
      ) {
        console.warn('[custom.js] Suppressed JSON.parse syntax error:', err.message);
        return null;
      }
      throw err;
    }
  };

  /* ==========================================================================
     2. Global Error Shield (Suppresses fatal uncaught crashes)
     ========================================================================== */
  window.addEventListener('error', function (event) {
    const msg = event && event.message ? String(event.message) : '';
    if (
      msg.includes('Missing shadow root') ||
      msg.includes('Required ref') ||
      msg.includes('cartBubbleCount') ||
      msg.includes('Unexpected EOF') ||
      msg.includes('Unexpected end of JSON') ||
      msg.includes('Unexpected token <')
    ) {
      event.preventDefault();
      console.warn('[custom.js] Prevented fatal runtime error:', msg);
      return true;
    }
  });

  /* ==========================================================================
     3. Prevent "Missing shadow root" on <overflow-list>
     ========================================================================== */
  function hydrateOverflowList(el) {
    if (!el || el.tagName?.toLowerCase() !== 'overflow-list') return;
    if (el.shadowRoot) return;

    try {
      const template = el.querySelector(
        ':scope > template[shadowrootmode="open"], :scope > template[shadowroot="open"], template'
      );
      if (template && template.content) {
        const shadow = el.attachShadow({ mode: 'open' });
        shadow.append(template.content.cloneNode(true));
      } else {
        // Fallback hydration so critical.js slots check passes
        const shadow = el.attachShadow({ mode: 'open' });
        shadow.innerHTML = `
          <ul part="list">
            <slot></slot>
            <slot name="more" part="more" hidden>
              <li part="more"><button class="button" type="button" tabindex="0">More</button></li>
            </slot>
            <li part="placeholder" hidden></li>
          </ul>
          <div part="overflow">
            <ul part="overflow-list">
              <slot name="overflow"></slot>
            </ul>
          </div>
        `;
      }
    } catch (e) {
      console.warn('[custom.js] ShadowRoot attachment note:', e);
    }
  }

  function hydrateAllOverflowLists(root = document) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('overflow-list').forEach(hydrateOverflowList);
  }

  /* ==========================================================================
     4. Prevent "Required ref 'cartBubbleCount' not found" on <cart-icon>
     ========================================================================== */
  function ensureCartIconRefs(icon) {
    if (!icon || icon.tagName?.toLowerCase() !== 'cart-icon') return;

    let bubble = icon.querySelector('[ref="cartBubble"]');
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.setAttribute('ref', 'cartBubble');
      bubble.className = 'cart-bubble visually-hidden';
      icon.appendChild(bubble);
    }

    let bubbleText = bubble.querySelector('[ref="cartBubbleText"]');
    if (!bubbleText) {
      bubbleText = document.createElement('span');
      bubbleText.setAttribute('ref', 'cartBubbleText');
      bubbleText.className = 'cart-bubble__text';
      bubble.appendChild(bubbleText);
    }

    let bubbleCount = bubbleText.querySelector('[ref="cartBubbleCount"]');
    if (!bubbleCount) {
      bubbleCount = document.createElement('span');
      bubbleCount.setAttribute('ref', 'cartBubbleCount');
      bubbleCount.className = 'cart-bubble__text-count hidden';
      bubbleCount.setAttribute('aria-hidden', 'true');
      bubbleCount.textContent = '0';
      bubbleText.appendChild(bubbleCount);
    }
  }

  function ensureAllCartIcons(root = document) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('cart-icon').forEach(ensureCartIconRefs);
  }

  /* ==========================================================================
     5. Neutralize Navigation Blocker (Allow Checkout to proceed freely)
     ========================================================================== */
  window._isNavigatingToCheckout = false;

  // Protect window.addEventListener directly
  if (typeof window.addEventListener === 'function') {
    const origWindowAdd = window.addEventListener;
    window.addEventListener = function (type, listener, options) {
      if (type === 'beforeunload') {
        // Prevent any external scripts from blocking page exit or checkout navigation
        return;
      }
      return origWindowAdd.call(this, type, listener, options);
    };
  }

  // Protect EventTarget.prototype.addEventListener
  if (typeof EventTarget !== 'undefined' && EventTarget.prototype) {
    const originalAddEventListener = EventTarget.prototype.addEventListener;
    EventTarget.prototype.addEventListener = function (type, listener, options) {
      if (type === 'beforeunload' && (this === window || this === document)) {
        return;
      }
      return originalAddEventListener.call(this, type, listener, options);
    };
  }

  /* ==========================================================================
     6. Checkout & Buy Now Click / Submit Protections (Fixes Dead Clicks)
     ========================================================================== */
  let lastAcceleratedClickTime = 0;

  // Sync drawer empty class when cart items are updated
  function syncDrawerState() {
    const dialog = document.querySelector('.cart-drawer__dialog');
    const hasItems = document.querySelector('.cart-items__table tbody tr, .cart-item');
    if (dialog && hasItems) {
      dialog.classList.remove('cart-drawer--empty');
    }
  }

  // Intercept click on any checkout trigger in capture phase
  document.addEventListener(
    'click',
    function (event) {
      const target = event.target;
      if (!target || !(target instanceof Element)) return;

      const checkoutBtn = target.closest(
        '#checkout, [name="checkout"], .cart__checkout-button, a[href*="/checkout"]'
      );

      if (checkoutBtn) {
        window._isNavigatingToCheckout = true;

        const form = document.getElementById('cart-form') || checkoutBtn.closest('form');
        if (form) {
          // Guarantee that the checkout parameter exists
          if (!form.querySelector('input[name="checkout"]')) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'checkout';
            input.value = 'Checkout';
            form.appendChild(input);
          }
        }
      }

      // Accelerated Checkout / Buy Now buttons
      const acceleratedBtn = target.closest(
        '.shopify-payment-button, [data-testid="Checkout-button"], .shopify-payment-button__button'
      );

      if (acceleratedBtn) {
        window._isNavigatingToCheckout = true;
        lastAcceleratedClickTime = Date.now();
      }
    },
    true
  );

  // Capture submit events before other scripts
  document.addEventListener(
    'submit',
    function (event) {
      const form = event.target;
      if (!form || !(form instanceof HTMLFormElement)) return;

      const submitter = event.submitter;
      const isCheckoutSubmit =
        submitter?.name === 'checkout' ||
        submitter?.id === 'checkout' ||
        submitter?.classList?.contains('cart__checkout-button') ||
        form.id === 'cart-form' ||
        form.action?.includes('/checkout');

      const isAcceleratedSubmit =
        Date.now() - lastAcceleratedClickTime < 2500 ||
        submitter?.closest?.('.shopify-payment-button') ||
        submitter?.classList?.contains('shopify-payment-button__button');

      if (isCheckoutSubmit) {
        window._isNavigatingToCheckout = true;
        if (!form.querySelector('input[name="checkout"]')) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'checkout';
          input.value = 'Checkout';
          form.appendChild(input);
        }
        event.stopImmediatePropagation();
        return;
      }

      if (isAcceleratedSubmit) {
        window._isNavigatingToCheckout = true;
        if (!form.querySelector('input[name="return_to"]')) {
          const returnTo = document.createElement('input');
          returnTo.type = 'hidden';
          returnTo.name = 'return_to';
          returnTo.value = '/checkout';
          form.appendChild(returnTo);
        }
        event.stopImmediatePropagation();
        return;
      }
    },
    true
  );

  /* ==========================================================================
     7. Dynamic DOM Mutation Observer (Protects morphing and AJAX sections)
     ========================================================================== */
  const observer = new MutationObserver(function (mutations) {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1) {
            if (node.tagName?.toLowerCase() === 'overflow-list') {
              hydrateOverflowList(node);
            } else if (node.querySelectorAll) {
              hydrateAllOverflowLists(node);
            }

            if (node.tagName?.toLowerCase() === 'cart-icon') {
              ensureCartIconRefs(node);
            } else if (node.querySelectorAll) {
              ensureAllCartIcons(node);
            }
          }
        }
      }
    }
    syncDrawerState();
  });

  // Run initial passes
  hydrateAllOverflowLists(document);
  ensureAllCartIcons(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      hydrateAllOverflowLists(document);
      ensureAllCartIcons(document);
      syncDrawerState();
      if (document.body) {
        observer.observe(document.body, { childList: true, subtree: true });
      }
    });
  } else {
    syncDrawerState();
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  // Listen to theme cart updates to keep drawer state in sync
  document.addEventListener('cart:update', syncDrawerState);
})();
